/** Stock transfers and count accuracy. Keys are flat, dot-namespaced and stable; placeholders use {name}. */
export const enStokTransfers = {
  // ---- Transfers: list --------------------------------------------------------------------
  'stok.transfers.title': 'Transfers',
  'stok.transfers.subtitle':
    'Move stock between locations of {branch} or to another branch. Value moves at average cost.',
  'stok.transfers.new': 'New transfer',
  'stok.transfers.noRead': 'Viewing transfers needs the inventory read or transfer permission.',
  'stok.transfers.readOnly':
    'You can view transfers. Creating, dispatching and cancelling them needs the transfer permission.',
  'stok.transfers.directionLabel': 'Direction',
  'stok.transfers.outgoing': 'Outgoing',
  'stok.transfers.incoming': 'Incoming',
  'stok.transfers.outgoingDesc':
    'Created at this branch: moves between its own locations and shipments to other branches.',
  'stok.transfers.incomingDesc': 'Sent here by other branches — receive them once they arrive.',
  'stok.transfers.listLabel': 'Transfers',
  'stok.transfers.emptyOutgoing':
    'No transfers yet. Create one to move stock to another location or branch.',
  'stok.transfers.emptyReadOnly': 'No transfers yet. They appear here once someone creates one.',
  'stok.transfers.emptyIncoming':
    'Nothing incoming. Transfers other branches dispatch to this branch appear here.',
  'stok.transfers.search': 'Number, location or branch',
  'stok.transfers.colNumber': 'Number',
  'stok.transfers.colRoute': 'From → to',
  'stok.transfers.colLines': 'Lines',
  'stok.transfers.colDates': 'Dates',
  'stok.transfers.fromBranch': 'From branch',
  'stok.transfers.allBranches': 'All branches',
  'stok.transfers.interBranch': 'To another branch',
  'stok.transfers.intraBranch': 'Within the branch',
  'stok.transfers.to': 'to',
  'stok.transfers.valueAtDispatch': 'Valued at dispatch',
  'stok.transfers.createdAt': 'Created {time}',
  'stok.transfers.dispatchedAt': 'Dispatched {time}',
  'stok.transfers.receivedAt': 'Received {time}',
  'stok.transfers.dispatched': 'Dispatched',
  'stok.transfers.received': 'Received',
  'stok.transfers.status.draft': 'Draft',
  'stok.transfers.status.in_transit': 'In transit',
  'stok.transfers.status.received': 'Received',
  'stok.transfers.status.cancelled': 'Cancelled',

  // ---- Transfers: new ---------------------------------------------------------------------
  'stok.transfers.back': 'Back to transfers',
  'stok.transfers.newTitle': 'New transfer',
  'stok.transfers.newSubtitle':
    'Saved as a draft. Stock moves only when the transfer is dispatched.',
  'stok.transfers.noPermission': 'Creating transfers needs the transfer permission.',
  'stok.transfers.noItems': 'There are no active items yet. Add items in master data first.',
  'stok.transfers.noLocations': 'This branch has no active stock locations yet.',
  'stok.transfers.routeTitle': 'Route',
  'stok.transfers.routeDesc': 'Where the stock leaves from and where it goes.',
  'stok.transfers.from': 'From',
  'stok.transfers.fromHint': 'A location of {branch}.',
  'stok.transfers.toBranch': 'Destination branch',
  'stok.transfers.toBranchHint': 'This branch, or another branch you work in (same currency).',
  'stok.transfers.toBranchOnly': 'You work in this branch only.',
  'stok.transfers.thisBranch': '{name} (this branch)',
  'stok.transfers.toLocation': 'To',
  'stok.transfers.pickLocation': 'Choose a location',
  'stok.transfers.toHintSame': 'Another location of this branch.',
  'stok.transfers.toHintOther': 'A location of {branch}.',
  'stok.transfers.destLoading': 'Loading locations…',
  'stok.transfers.destFailed': 'The locations of this branch could not be loaded. Try again.',
  'stok.transfers.destForbidden':
    'You cannot see the locations of this branch (it needs inventory read rights there).',
  'stok.transfers.destNone': 'No other active location to choose.',
  'stok.transfers.interNote':
    'Stock leaves this branch at dispatch and is in transit until {branch} receives it.',
  'stok.transfers.linesTitle': 'Lines',
  'stok.transfers.linesDesc':
    'Up to {max} lines. For lot items, leave the lot empty to take the earliest expiry first (FEFO).',
  'stok.transfers.lineLabel': 'Line {n}',
  'stok.transfers.removeLine': 'Remove line {n}',
  'stok.transfers.addLine': 'Add line',
  'stok.transfers.pickItem': 'Choose an item',
  'stok.transfers.inBase': 'In the base unit:',
  'stok.transfers.fefo': 'Automatic (FEFO)',
  'stok.transfers.lotHint': 'Lots at the source location with expiry and quantity.',
  'stok.transfers.lotsNone': 'No lot stock at this location — FEFO will find none.',
  'stok.transfers.lotsFailed': 'Lots could not be loaded; FEFO still works.',
  'stok.transfers.blockExpired': 'Expired',
  'stok.transfers.fefoTakes': 'FEFO would take now: {lots}.',
  'stok.transfers.fefoShort': 'Usable lot stock is short by {qty} — dispatch will be refused.',
  'stok.transfers.fefoBlocked': '{count} blocked lot(s) are never used automatically.',
  'stok.transfers.overrideReason': 'Override reason',
  'stok.transfers.overrideHintExpired':
    'This lot is expired. Say why it may move anyway, e.g. “To quarantine area”. Recorded in the audit log.',
  'stok.transfers.overrideHintHeld':
    'This lot is on hold. Say why it may move anyway, e.g. “To quarantine area”. Recorded in the audit log.',
  'stok.transfers.blockedNoRightExpired':
    'This lot is expired. Moving it needs the inventory approval permission and a reason — choose another lot, leave it on FEFO or ask a manager.',
  'stok.transfers.blockedNoRightHeld':
    'This lot is on hold. Moving it needs the inventory approval permission and a reason — choose another lot, leave it on FEFO or ask a manager.',
  'stok.transfers.notes': 'Notes',
  'stok.transfers.notesHint': 'For the people dispatching and receiving, up to 300 characters.',
  'stok.transfers.draftNote': 'A draft does not move stock yet.',
  'stok.transfers.saveDraft': 'Save draft',
  'stok.transfers.created': 'Transfer {number} saved as a draft.',
  'stok.transfers.err.from': 'Choose where the stock leaves from.',
  'stok.transfers.err.toBranch': 'Choose the destination branch.',
  'stok.transfers.err.to': 'Choose the destination location.',
  'stok.transfers.err.sameLocation': 'Choose a location other than the source.',
  'stok.transfers.err.notes': 'Use at most 300 characters.',
  'stok.transfers.err.noLines': 'Add at least one line.',
  'stok.transfers.err.tooManyLines': 'Use at most 100 lines.',
  'stok.transfers.err.item': 'Choose an item.',
  'stok.transfers.err.qty': 'Enter a quantity above 0.',
  'stok.transfers.err.unit': 'Choose a unit.',
  'stok.transfers.err.precision':
    'This quantity cannot be converted exactly to the base unit. Use fewer decimals.',
  'stok.transfers.err.override': 'Write an override reason of at least 3 characters.',
  'stok.transfers.err.overrideLong': 'Use at most 300 characters.',
  'stok.transfers.err.blockedNoRight':
    'This lot is blocked. Only someone with the inventory approval permission can move it.',
  'stok.transfers.err.blockedForbidden':
    'Moving a held or expired lot needs the inventory approval permission.',
  'stok.transfers.err.lotExpired':
    'A chosen lot has expired. Give an override reason or choose another lot.',
  'stok.transfers.err.lotOnHold':
    'A chosen lot is on hold. Give an override reason or choose another lot.',
  'stok.transfers.err.insufficient': 'Not enough stock at the source location.',
  'stok.transfers.err.insufficientQty':
    'Not enough {item} at the source: {available} available, {requested} needed.',
  'stok.transfers.err.insufficientLotQty':
    'Not enough usable lot stock of {item} (expired and held lots are excluded): {available} available, {requested} needed.',
  'stok.transfers.err.state': 'This transfer changed in the meantime. The latest state is shown.',

  // ---- Transfers: detail ------------------------------------------------------------------
  'stok.transfers.detailTitle': 'Transfer',
  'stok.transfers.factsTitle': 'Details',
  'stok.transfers.kind': 'Kind',
  'stok.transfers.createdBy': 'Created by',
  'stok.transfers.dispatchedBy': 'Dispatched by',
  'stok.transfers.receivedBy': 'Received by',
  'stok.transfers.linesCount': '{count} line(s)',
  'stok.transfers.fefoShortLabel': 'FEFO',
  'stok.transfers.override': 'Override',
  'stok.transfers.total': 'Total',
  'stok.transfers.valueNote':
    'Values are set at dispatch from the average cost; the receiving side gets exactly that value.',
  'stok.transfers.actionsTitle': 'Next step',
  'stok.transfers.stepDraft': 'Dispatch moves the stock to the destination location at once.',
  'stok.transfers.stepDraftInter':
    'Dispatch takes the stock out of this branch; the destination branch receives it.',
  'stok.transfers.stepInTransit': 'In transit — waiting for {branch} to receive it.',
  'stok.transfers.stepReceived': 'Done: the stock is at the destination.',
  'stok.transfers.stepCancelled': 'Cancelled before dispatch; no stock moved.',
  'stok.transfers.dispatch': 'Dispatch',
  'stok.transfers.dispatching': 'Dispatching…',
  'stok.transfers.dispatchTitle': 'Dispatch {number}?',
  'stok.transfers.dispatchBody': '{count} line(s): {route}.',
  'stok.transfers.dispatchIntraWarning':
    'Stock moves now and the ledger records both sides. This cannot be undone.',
  'stok.transfers.dispatchInterWarning':
    'Stock leaves this branch now and stays in transit until {branch} receives it. This cannot be undone.',
  'stok.transfers.dispatchedDone': 'Transfer {number} dispatched and received.',
  'stok.transfers.dispatchedInTransit': 'Transfer {number} dispatched — now in transit.',
  'stok.transfers.receive': 'Receive',
  'stok.transfers.receiving': 'Receiving…',
  'stok.transfers.receiveTitle': 'Receive {number}?',
  'stok.transfers.receiveBody': 'Stock from {from} is booked into {location}.',
  'stok.transfers.receiveWarning':
    'Receive once the goods have arrived. Lots keep their code and expiry.',
  'stok.transfers.receiveReadOnly': 'Receiving needs the transfer or receiving permission here.',
  'stok.transfers.receivedDone': 'Transfer {number} received.',
  'stok.transfers.awaitingReceipt':
    'Receiving happens at {branch}: someone working there confirms it.',
  'stok.transfers.openAtDestination': 'Switch to {branch}',
  'stok.transfers.draftElsewhere': 'Draft of {branch}; it is dispatched there.',
  'stok.transfers.cancelDraft': 'Cancel draft',
  'stok.transfers.cancelling': 'Cancelling…',
  'stok.transfers.cancelTitle': 'Cancel {number}?',
  'stok.transfers.cancelBody': 'The draft is closed and no stock moves.',
  'stok.transfers.cancelWarning': 'A cancelled transfer cannot be dispatched any more.',
  'stok.transfers.cancelledDone': 'Transfer {number} cancelled.',
  'stok.transfers.viewLedger': 'View in the stock ledger',
  'stok.transfers.resultTitle': 'Posted movements',
  'stok.transfers.resultDesc': 'What this step wrote to the stock ledger.',
  'stok.transfers.resultEmpty': 'No movements were written.',

  // ---- Count: add found items / lots ------------------------------------------------------
  'stok.countAdd.open': 'Add found item / lot',
  'stok.countAdd.title': 'Add found items or lots',
  'stok.countAdd.desc':
    'Stock found at {location} that is not on the count sheet. It is added with an expected quantity of 0 — count it like any other line.',
  'stok.countAdd.rowLabel': 'Found {n}',
  'stok.countAdd.removeRow': 'Remove found {n}',
  'stok.countAdd.addRow': 'Another one',
  'stok.countAdd.pickItem': 'Choose an item',
  'stok.countAdd.noLot': 'Not lot-tracked: counted as one line.',
  'stok.countAdd.pickLot': 'Choose a lot',
  'stok.countAdd.lotLoading': 'Loading lots…',
  'stok.countAdd.lotHint':
    'Existing lots of this branch, also empty ones. A new lot is recorded by receiving, not by counting.',
  'stok.countAdd.lotNone': 'Every lot of this item is already on the sheet, or it has none yet.',
  'stok.countAdd.lotExpiry': 'expires {date}',
  'stok.countAdd.lotHere': '{qty} here',
  'stok.countAdd.noItems': 'Every active item is already on the sheet.',
  'stok.countAdd.note': 'Only while the count is in progress (not during a recount).',
  'stok.countAdd.submit': 'Add to the sheet',
  'stok.countAdd.added': '{count} line(s) added to the sheet.',
  'stok.countAdd.err.item': 'Choose an item.',
  'stok.countAdd.err.lot': 'Choose the lot you found.',
  'stok.countAdd.err.onSheet': 'Already on the count sheet.',
  'stok.countAdd.err.duplicate': 'Listed twice.',
  'stok.countAdd.err.none': 'Add at least one item.',

  // ---- Count accuracy report --------------------------------------------------------------
  'stok.countAccuracy.title': 'Stock accuracy',
  'stok.countAccuracy.subtitle': 'How closely posted stock counts at {branch} matched the books.',
  'stok.countAccuracy.rangeTitle': 'Period',
  'stok.countAccuracy.rangeDesc':
    'Counts whose adjustment was posted on these business dates ({timeZone}).',
  'stok.countAccuracy.period': '{from} – {to}',
  'stok.countAccuracy.accuracy': 'Accuracy',
  'stok.countAccuracy.accuracyDesc': 'Lines within tolerance ÷ lines evaluated.',
  'stok.countAccuracy.ofLines': '{within} of {evaluated} lines',
  'stok.countAccuracy.noneEvaluated': 'No lines were evaluated in this period.',
  'stok.countAccuracy.counts': 'Posted counts',
  'stok.countAccuracy.countsDesc': 'Stock counts posted in the period.',
  'stok.countAccuracy.variance': 'Variance value',
  'stok.countAccuracy.varianceDesc': 'Counted minus expected, at average cost ({currency}).',
  'stok.countAccuracy.varianceHint': 'Negative = stock lost.',
  'stok.countAccuracy.excluded': 'Excluded lines',
  'stok.countAccuracy.excludedDesc': 'Not in the denominator.',
  'stok.countAccuracy.excludedHint':
    'Lines never compared with the books (e.g. not counted before posting) do not count for or against accuracy.',
  'stok.countAccuracy.excludedNone': 'Every line of these counts was evaluated.',
  'stok.countAccuracy.byLocation': 'By location',
  'stok.countAccuracy.byLocationDesc': 'Accuracy per counted location.',
  'stok.countAccuracy.colCounts': 'Counts',
  'stok.countAccuracy.colEvaluated': 'Lines evaluated',
  'stok.countAccuracy.empty':
    'No posted counts in this period. Accuracy appears once stock counts are approved and posted.',
  'stok.countAccuracy.def.scope': 'Scope',
  'stok.countAccuracy.def.accuracy': 'Accuracy',
  'stok.countAccuracy.def.excluded': 'Excluded lines',
  'stok.countAccuracy.def.varianceValue': 'Variance value',
} as const;
